import {
  MenuEmpty,
  MenuInput,
  MenuItems,
  SelectMenu,
  SelectMenuContent,
  SelectMenuItem,
  SelectMenuProvider,
  SelectMenuTriggerType,
  Spinner,
} from '@cint/wonderment';

import { useCompanyPanelSelector } from './useCompanyPanelSelector';

interface CompanyPanelSelectorProps {
  currentCompanyId?: string;
  currentPanelId?: string;
  currentPanelName?: string;
  onNavigate?: (companyId: string, panelId: string) => void;
  basePath?: string;
}

export const CompanyPanelSelector = ({
  basePath = '',
  ...rest
}: CompanyPanelSelectorProps) => {
  const {
    isCompaniesLoading,
    isSingleCompany,
    panelSelectorProps,
    companySelectorProps,
    setPendingCompanyId,
  } = useCompanyPanelSelector({ ...rest, basePath });

  if (isCompaniesLoading) return <Spinner />;

  const {
    displayLabel,
    activePanelId,
    panelsReady,
    panelsListOptions,
    onPanelSelectedChange,
  } = panelSelectorProps;

  const {
    companiesListOptions,
    activeCompanyId,
    onCompanyPanelSelectedChange,
  } = companySelectorProps;

  const selectedCompanyName =
    companiesListOptions?.find(({ value }) => value === activeCompanyId)
      ?.label || 'Select company';

  const handleSelectCompany = (values: string[]) => {
    const companyId = values[0];
    if (!companyId) return;
    setPendingCompanyId(companyId);
    companySelectorProps.onCompanyMouseEnter(companyId);
  };

  const handleSelectPanel = (values: string[]) => {
    const panelId = values[0];
    if (!panelId) return;
    if (isSingleCompany) {
      onPanelSelectedChange([panelId]);
    } else {
      onCompanyPanelSelectedChange(activeCompanyId)([panelId]);
    }
  };

  return (
    <div>
      {!isSingleCompany && (
        <SelectMenuProvider
          isMultiSelect={false}
          onSelectedChange={handleSelectCompany}
          selectedItems={activeCompanyId ? [activeCompanyId] : []}
        >
          <SelectMenu
            triggerProps={{
              type: SelectMenuTriggerType.InlineSelect,
              props: {
                text: selectedCompanyName,
                fontWeight: 'normal',
                size: 'h6',
              },
            }}
          >
            <SelectMenuContent>
              <MenuInput placeholder="Search for company name" />
              {!companiesListOptions?.length && (
                <MenuEmpty>No companies found.</MenuEmpty>
              )}
              <MenuItems>
                {companiesListOptions?.map(({ label, value, totalPanels }) => (
                  <SelectMenuItem
                    key={value}
                    label={label}
                    hint={`${totalPanels}`}
                    value={value}
                    isDisabled={!totalPanels}
                  />
                ))}
              </MenuItems>
            </SelectMenuContent>
          </SelectMenu>
        </SelectMenuProvider>
      )}

      <div className={isSingleCompany ? '' : 'mt-4'}>
        {!panelsReady && <Spinner />}
        {panelsReady && !panelsListOptions?.length && (
          <p>No panels found for this company.</p>
        )}
        {panelsReady && !!panelsListOptions?.length && (
          <SelectMenuProvider
            isMultiSelect={false}
            onSelectedChange={handleSelectPanel}
            selectedItems={activePanelId ? [activePanelId] : []}
          >
            <SelectMenu
              triggerProps={
                isSingleCompany
                  ? {
                      type: SelectMenuTriggerType.InlineSelect,
                      props: {
                        text: displayLabel,
                        fontWeight: 'normal',
                        size: 'h3',
                      },
                    }
                  : {
                      type: SelectMenuTriggerType.SelectButton,
                      props: {
                        label: displayLabel,
                      },
                    }
              }
            >
              <SelectMenuContent>
                <MenuInput placeholder="Search for panel name" />
                <MenuEmpty>No panels found.</MenuEmpty>
                <MenuItems>
                  {panelsListOptions.map(({ label, value }) => (
                    <SelectMenuItem key={value} label={label} value={value} />
                  ))}
                </MenuItems>
              </SelectMenuContent>
            </SelectMenu>
          </SelectMenuProvider>
        )}
      </div>
    </div>
  );
};
